'use client';

import type { GeneratorRepeatPayload } from '@repo/shared';

const GENERATOR_BAR_REPEAT_STORAGE_KEY = 'generator-bar-repeat';
const GENERATOR_BAR_REPEAT_EVENT = 'generator-bar-repeat';
const GENERATOR_BAR_VISIBILITY_EVENT = 'generator-bar-visibility';
const GENERATOR_BAR_ATTENTION_EVENT = 'generator-bar-attention';
const GENERATOR_BAR_PROMPT_EVENT = 'generator-bar-prompt';
const GENERATOR_BAR_MODEL_CHANGED_EVENT = 'generator-bar-model-changed';

/** Hint to the dock that it should grab the user's eye — used after we
 * push a fresh repeat payload from a deep view (idea/trend/generation
 * preview modal). The dock does a small spring-bounce + pulses any
 * follow-up affordances (e.g. the "+ Photo" tile) so the user sees that
 * something just arrived. */
export interface GeneratorBarAttentionPayload {
  /** When true, also pulse the photo upload chip. Defaults to false. */
  pulsePhoto?: boolean;
}

export function dispatchGeneratorBarRepeat(payload: GeneratorRepeatPayload): void {
  window.dispatchEvent(
    new CustomEvent<GeneratorRepeatPayload>(GENERATOR_BAR_REPEAT_EVENT, {
      detail: payload,
    }),
  );
}

export function subscribeGeneratorBarRepeat(
  callback: (payload: GeneratorRepeatPayload) => void,
): () => void {
  const handler = (event: Event) => {
    callback((event as CustomEvent<GeneratorRepeatPayload>).detail);
  };

  window.addEventListener(GENERATOR_BAR_REPEAT_EVENT, handler);
  return () => window.removeEventListener(GENERATOR_BAR_REPEAT_EVENT, handler);
}

export function storeGeneratorBarRepeat(payload: GeneratorRepeatPayload): void {
  window.sessionStorage.setItem(
    GENERATOR_BAR_REPEAT_STORAGE_KEY,
    JSON.stringify(payload),
  );
}

export function consumeStoredGeneratorBarRepeat(): GeneratorRepeatPayload | null {
  const raw = window.sessionStorage.getItem(GENERATOR_BAR_REPEAT_STORAGE_KEY);
  if (!raw) return null;

  window.sessionStorage.removeItem(GENERATOR_BAR_REPEAT_STORAGE_KEY);

  try {
    return JSON.parse(raw) as GeneratorRepeatPayload;
  } catch {
    return null;
  }
}

export function dispatchGeneratorBarVisibility(visible: boolean): void {
  window.dispatchEvent(
    new CustomEvent<boolean>(GENERATOR_BAR_VISIBILITY_EVENT, { detail: visible }),
  );
}

export function subscribeGeneratorBarVisibility(
  callback: (visible: boolean) => void,
): () => void {
  const handler = (event: Event) => {
    callback((event as CustomEvent<boolean>).detail);
  };

  window.addEventListener(GENERATOR_BAR_VISIBILITY_EVENT, handler);
  return () => window.removeEventListener(GENERATOR_BAR_VISIBILITY_EVENT, handler);
}

export function dispatchGeneratorBarAttention(
  payload: GeneratorBarAttentionPayload = {},
): void {
  window.dispatchEvent(
    new CustomEvent<GeneratorBarAttentionPayload>(GENERATOR_BAR_ATTENTION_EVENT, {
      detail: payload,
    }),
  );
}

export function subscribeGeneratorBarAttention(
  callback: (payload: GeneratorBarAttentionPayload) => void,
): () => void {
  const handler = (event: Event) => {
    callback((event as CustomEvent<GeneratorBarAttentionPayload>).detail);
  };

  window.addEventListener(GENERATOR_BAR_ATTENTION_EVENT, handler);
  return () => window.removeEventListener(GENERATOR_BAR_ATTENTION_EVENT, handler);
}

/** Drop-in prompt replacement — overwrites only the textarea text in
 * the dock without touching the chosen type, model, or attached
 * images. Use this when a deep view picks an idea and the user just
 * wants the wording, not a full repeat. */
export function dispatchGeneratorBarPrompt(prompt: string): void {
  window.dispatchEvent(
    new CustomEvent<string>(GENERATOR_BAR_PROMPT_EVENT, { detail: prompt }),
  );
}

export function subscribeGeneratorBarPrompt(
  callback: (prompt: string) => void,
): () => void {
  const handler = (event: Event) => {
    callback((event as CustomEvent<string>).detail);
  };

  window.addEventListener(GENERATOR_BAR_PROMPT_EVENT, handler);
  return () => window.removeEventListener(GENERATOR_BAR_PROMPT_EVENT, handler);
}

/* Broadcast the dock's currently-selected model slug. Listeners — for
 * example AppSidebar's "Популярное" list, which highlights the active
 * row — sync to the dock's truth without prop-drilling state across
 * the whole tree. Fires on every change, including the initial mount
 * value, so subscribers can hydrate from the latest dispatch. */
export function dispatchGeneratorBarModelChanged(slug: string | null): void {
  window.dispatchEvent(
    new CustomEvent<string | null>(GENERATOR_BAR_MODEL_CHANGED_EVENT, {
      detail: slug,
    }),
  );
}

export function subscribeGeneratorBarModelChanged(
  callback: (slug: string | null) => void,
): () => void {
  const handler = (event: Event) => {
    callback((event as CustomEvent<string | null>).detail);
  };

  window.addEventListener(GENERATOR_BAR_MODEL_CHANGED_EVENT, handler);
  return () =>
    window.removeEventListener(GENERATOR_BAR_MODEL_CHANGED_EVENT, handler);
}
